import { Fragment, useEffect, useRef, useState } from 'react';
import type { Session } from '../lib/session';
import { can, isOwner } from '../lib/session';
import { ApiError } from '../lib/api';
import { formatDate } from '../lib/money';
import type { PermitInput, PermitRow } from '../lib/permits';
import { permitsApi, whenLabel } from '../lib/permits';
import type { PlantRow } from '../lib/roster';
import { rosterApi } from '../lib/roster';
import type { CostCode } from '../lib/costcodes';
import { codeOf, costCodeApi } from '../lib/costcodes';
import CostCodeSelect from '../components/CostCodeSelect';

/**
 * The Permits tab: two sections, Permits and Plants.
 *
 * Asked for by the owner on 23 September 2026 and shaped on a mockup he tried:
 * "a separate tab as permits and plants — they will have a field to enter
 * permit details and expiry, and all reminders will display". Plants moved
 * here from Settings, and a road vehicle carries its papers — revenue
 * licence, insurance, emission test, fitness — each with an expiry date.
 * Every plant, road vehicle or not, may carry its next service date (31
 * October 2026); its cost is a misc expense, not kept here.
 *
 * Who may do what: the owner everything. A clerk with the `plants`
 * permission reaches the tab, may add and edit plants (the permission she
 * already had), and sees the permits read-only. The reminders themselves
 * are the ExpiringSoon component, on Approvals for the owner and above the
 * day for the office.
 *
 * Changing an expiry date is a renewal: the old date is kept and shown as
 * "renewed 23 Sept 2026 — was 30 Sept 2026".
 */

interface Props {
  session: Session;
  today: string;
}

type Segment = 'permits' | 'plants';

const PAPERS = [
  { key: 'licenceExpiresOn', label: 'Revenue licence', sub: 'Pradeshiya Sabha, yearly' },
  { key: 'insuranceExpiresOn', label: 'Insurance', sub: 'Insurer and policy on the certificate' },
  { key: 'emissionExpiresOn', label: 'Emission test', sub: 'VET, yearly' },
  { key: 'fitnessExpiresOn', label: 'Fitness certificate', sub: 'Lorries and buses only' },
] as const;

type PaperKey = (typeof PAPERS)[number]['key'];

function daysLeft(today: string, date: string): number {
  return Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
}

/** A service is overdue, not expired. */
function pillFor(days: number | null, window: number, service = false): { className: string; text: string } {
  if (days === null) return { className: 'pill', text: 'no date' };
  if (days < 0) return { className: 'pill p-no', text: service ? 'overdue' : 'expired' };
  if (days <= 7) return { className: 'pill p-no', text: `${days} days` };
  if (days <= window) return { className: 'pill p-wait', text: `${days} days` };
  return { className: 'pill p-ok', text: `${days} days` };
}

export default function Permits({ session, today }: Props) {
  const owner = isOwner(session);
  const mayEditPlants = owner || can(session, 'plants');
  const [segment, setSegment] = useState<Segment>('permits');
  const [permits, setPermits] = useState<PermitRow[]>([]);
  const [plants, setPlants] = useState<PlantRow[]>([]);
  const [costCodes, setCostCodes] = useState<CostCode[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const [p, pl, cc] = await Promise.all([
        permitsApi.list(), rosterApi.listPlants(), costCodeApi.list(),
      ]);
      setPermits(p.permits);
      setPlants(pl.plants);
      setCostCodes(cc.costCodes.map((row) => ({
        id: row.id, code: row.code, name: row.name, is_active: row.isActive ? 1 : 0,
      })));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the office. Permits need a signal.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function act(work: () => Promise<unknown>) {
    setError('');
    try {
      await work();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
      throw err;
    }
  }

  if (loading) return <div className="empty">Loading…</div>;

  return (
    <>
      <div className="bar" style={{ margin: '0 0 12px' }}>
        <button type="button" className={`btn ${segment === 'permits' ? 'pri' : ''}`} style={{ flex: 1 }} onClick={() => setSegment('permits')}>
          Permits
        </button>
        <button type="button" className={`btn ${segment === 'plants' ? 'pri' : ''}`} style={{ flex: 1 }} onClick={() => setSegment('plants')}>
          Plants
        </button>
      </div>

      {error && <div className="banner b-no">{error}</div>}

      {segment === 'permits' ? (
        <PermitsSection permits={permits} today={today} canEdit={owner} act={act} />
      ) : (
        <PlantsSection plants={plants} costCodes={costCodes} today={today} canEdit={mayEditPlants} act={act} />
      )}
    </>
  );
}

// ================================================================ permits

function PermitsSection({
  permits, today, canEdit, act,
}: {
  permits: PermitRow[];
  today: string;
  canEdit: boolean;
  act: (work: () => Promise<unknown>) => Promise<void>;
}) {
  const [editing, setEditing] = useState<PermitRow | 'new' | null>(null);

  return (
    <>
      <div className="card">
        <h3>Permits and licences</h3>
        <div className="sub">
          Everything the quarry needs to be allowed to operate. A reminder appears before each
          one runs out — on Approvals for the owner, above the day for the office.
          {canEdit ? ' Renewing keeps the old date as history.' : ' Only the owner can add or renew one.'}
        </div>

        {permits.length === 0 && <div className="empty">No permits yet.{canEdit && ' Add the first one below.'}</div>}
        {permits.map((permit) => {
          const pill = pillFor(permit.daysLeft, permit.remindDays);
          const last = permit.renewals[0];
          return (
            <div className="row" key={permit.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="name">{permit.name}</div>
                <div className="sub">
                  {[permit.authority, permit.refNo].filter(Boolean).join(' · ')}
                  {(permit.authority || permit.refNo) && ' · '}
                  {permit.daysLeft < 0 ? 'ran out ' : 'expires '}
                  {formatDate(permit.expiresOn)} · reminder {permit.remindDays} days before
                </div>
                {last && (
                  <div className="sub">
                    Renewed {formatDate(last.renewedAt.slice(0, 10))} — was {formatDate(last.previousExpiresOn)}
                    {last.by && ` · by ${last.by}`}
                  </div>
                )}
              </div>
              <span className={pill.className}>{pill.text}</span>
              {canEdit && (
                <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => setEditing(permit)}>
                  Edit ›
                </button>
              )}
            </div>
          );
        })}

        {canEdit && editing === null && (
          <div className="bar" style={{ marginTop: 12 }}>
            <button type="button" className="btn pri" onClick={() => setEditing('new')}>
              Add a permit
            </button>
          </div>
        )}
      </div>

      {canEdit && editing !== null && (
        <PermitEditor
          permit={editing === 'new' ? null : editing}
          today={today}
          onCancel={() => setEditing(null)}
          onSave={async (body) => {
            const current = editing === 'new' ? null : editing;
            await act(async () => {
              if (!current) await permitsApi.create(body);
              else await permitsApi.update(current.id, { ...body, rowVersion: current.rowVersion });
            });
            setEditing(null);
          }}
          onRemove={
            editing === 'new'
              ? null
              : async () => {
                  const current = editing;
                  await act(() => permitsApi.remove(current.id));
                  setEditing(null);
                }
          }
        />
      )}
    </>
  );
}

function PermitEditor({
  permit, today, onCancel, onSave, onRemove,
}: {
  permit: PermitRow | null;
  today: string;
  onCancel: () => void;
  onSave: (body: PermitInput) => Promise<void>;
  onRemove: (() => Promise<void>) | null;
}) {
  const [name, setName] = useState(permit?.name ?? '');
  const [authority, setAuthority] = useState(permit?.authority ?? '');
  const [refNo, setRefNo] = useState(permit?.refNo ?? '');
  const [expiresOn, setExpiresOn] = useState(permit?.expiresOn ?? '');
  const [remindDays, setRemindDays] = useState(String(permit?.remindDays ?? 30));
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const renewing = permit !== null && expiresOn !== '' && expiresOn !== permit.expiresOn;

  return (
    <div className="card" style={{ borderColor: 'var(--accent)' }}>
      <h3 style={{ marginBottom: 10 }}>{permit ? 'Edit permit' : 'Add a permit'}</h3>

      <div className="sub" style={{ marginBottom: 4 }}>What it is</div>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Environmental Protection Licence" />

      <div className="sub" style={{ margin: '10px 0 4px' }}>Issued by</div>
      <input type="text" value={authority} onChange={(e) => setAuthority(e.target.value)} placeholder="CEA, GSMB, Pradeshiya Sabha…" />

      <div className="sub" style={{ margin: '10px 0 4px' }}>Licence or reference number</div>
      <input type="text" value={refNo} onChange={(e) => setRefNo(e.target.value)} placeholder="EPL/NW/1042" />

      <div className="fld" style={{ marginTop: 10 }}>
        <div>
          Expires on
          {renewing && <div className="sub">Was {formatDate(permit!.expiresOn)} — kept as history</div>}
        </div>
        <input type="date" value={expiresOn} min={permit ? undefined : today} onChange={(e) => setExpiresOn(e.target.value)} style={{ width: 170 }} />
      </div>

      <div className="fld">
        <div>Remind from</div>
        <select value={remindDays} onChange={(e) => setRemindDays(e.target.value)} style={{ width: 170 }}>
          <option value="30">30 days before</option>
          <option value="60">60 days before</option>
          <option value="90">90 days before</option>
        </select>
      </div>

      {problem && <div className="banner b-no" style={{ marginTop: 8 }}>{problem}</div>}

      <div className="bar" style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
        {onRemove && (
          <button
            type="button"
            className="btn dang"
            disabled={saving}
            onClick={() => { setSaving(true); void onRemove().finally(() => setSaving(false)); }}
          >
            Remove
          </button>
        )}
        <button
          type="button"
          className="btn pri"
          disabled={saving || name.trim().length === 0 || expiresOn === ''}
          onClick={() => {
            setProblem('');
            if (!expiresOn) { setProblem('A permit needs an expiry date.'); return; }
            setSaving(true);
            void onSave({
              name: name.trim(),
              authority: authority.trim() || null,
              refNo: refNo.trim() || null,
              expiresOn,
              remindDays: Number(remindDays) || 30,
            })
              .catch(() => undefined)
              .finally(() => setSaving(false));
          }}
        >
          {saving ? 'Saving…' : renewing ? 'Save — renewed' : permit ? 'Save' : 'Save permit'}
        </button>
      </div>
    </div>
  );
}

// ================================================================= plants

function PlantsSection({
  plants, costCodes, today, canEdit, act,
}: {
  plants: PlantRow[];
  costCodes: CostCode[];
  today: string;
  canEdit: boolean;
  act: (work: () => Promise<unknown>) => Promise<void>;
}) {
  const [editing, setEditing] = useState<PlantRow | 'new' | null>(null);
  const active = plants.filter((p) => p.isActive);

  // The editor opens where it was asked for — under the plant tapped, or where
  // "Add plant" was — so nobody scrolls to the foot of the list to find it.
  const editor = (current: PlantRow | null) => (
    <PlantEditor
      key={current?.id ?? 'new'}
      plant={current}
      costCodes={costCodes}
      today={today}
      onCancel={() => setEditing(null)}
      onSave={async (body) => {
        await act(async () => {
          if (!current) await rosterApi.createPlant(body);
          else await rosterApi.updatePlant(current.id, { ...body, rowVersion: current.rowVersion });
        });
        setEditing(null);
      }}
      onRemove={
        current === null
          ? null
          : async () => {
              await act(() => rosterApi.removePlant(current.id));
              setEditing(null);
            }
      }
    />
  );

  return (
    <>
      <div className="card">
        <h3>Plants</h3>
        <div className="sub">
          Any machine or vehicle that costs money — excavators, loaders, tippers, the genset.
          Diesel and plant expenses are booked against these. A road vehicle also carries its
          papers. A reminder appears 30 days before a paper runs out or a service is due.
        </div>

        {active.length === 0 && <div className="empty">No plants yet.</div>}
        {active.map((plant) => {
          // the paper or service closest to falling due decides the pill
          let worst: { days: number; label: string; service: boolean } | null = null;
          for (const paper of plant.isRoadVehicle ? PAPERS : []) {
            const date = plant[paper.key];
            if (!date) continue;
            const days = daysLeft(today, date);
            if (worst === null || days < worst.days) worst = { days, label: paper.label.toLowerCase(), service: false };
          }
          if (plant.serviceDueOn) {
            const days = daysLeft(today, plant.serviceDueOn);
            if (worst === null || days < worst.days) worst = { days, label: 'service', service: true };
          }
          const pill = worst
            ? pillFor(worst.days, 30, worst.service)
            : plant.isRoadVehicle ? { className: 'pill', text: 'no dates' } : null;
          const due = worst ? ` · ${worst.label} ${whenLabel(worst.days, worst.service ? 'service' : 'paper')}` : '';
          const open = canEdit && editing !== null && editing !== 'new' && editing.id === plant.id;
          return (
            <Fragment key={plant.id}>
            <div className="row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="name">{plant.name}</div>
                <div className="sub">
                  {plant.code}
                  {codeOf(costCodes, plant.costCodeId) && ` · ${codeOf(costCodes, plant.costCodeId)}`}
                  {plant.isRoadVehicle
                    ? ` · ${plant.registrationNo ?? 'no plate yet'}${due}`
                    : ` · not a road vehicle${due}`}
                </div>
              </div>
              {pill && <span className={pill.className}>{pill.text}</span>}
              {canEdit && !open && (
                <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => setEditing(plant)}>
                  Edit ›
                </button>
              )}
            </div>
            {open && editor(plant)}
            </Fragment>
          );
        })}

        {canEdit && editing === null && (
          <div className="bar" style={{ marginTop: 12 }}>
            <button type="button" className="btn pri" onClick={() => setEditing('new')}>
              Add plant
            </button>
          </div>
        )}
        {canEdit && editing === 'new' && editor(null)}
      </div>
    </>
  );
}

function PlantEditor({
  plant, costCodes, today, onCancel, onSave, onRemove,
}: {
  plant: PlantRow | null;
  costCodes: CostCode[];
  today: string;
  onCancel: () => void;
  onSave: (body: {
    name: string; code: string; costCodeId: string | null;
    isRoadVehicle: boolean; registrationNo: string | null;
    licenceExpiresOn: string | null; insuranceExpiresOn: string | null;
    emissionExpiresOn: string | null; fitnessExpiresOn: string | null;
    serviceDueOn: string | null;
  }) => Promise<void>;
  onRemove: (() => Promise<void>) | null;
}) {
  const [name, setName] = useState(plant?.name ?? '');
  const [code, setCode] = useState(plant?.code ?? '');
  const [costCodeId, setCostCodeId] = useState<string | null>(plant?.costCodeId ?? null);
  const [road, setRoad] = useState(plant?.isRoadVehicle ?? false);
  const [reg, setReg] = useState(plant?.registrationNo ?? '');
  const [dates, setDates] = useState<Record<PaperKey, string>>({
    licenceExpiresOn: plant?.licenceExpiresOn ?? '',
    insuranceExpiresOn: plant?.insuranceExpiresOn ?? '',
    emissionExpiresOn: plant?.emissionExpiresOn ?? '',
    fitnessExpiresOn: plant?.fitnessExpiresOn ?? '',
  });
  const [serviceDueOn, setServiceDueOn] = useState(plant?.serviceDueOn ?? '');
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    box.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);
  const serviceDays = serviceDueOn ? daysLeft(today, serviceDueOn) : null;
  const servicePill = serviceDays === null ? null : pillFor(serviceDays, 30, true);

  return (
    <div className="card" ref={box} style={{ borderColor: 'var(--accent)' }}>
      <h3 style={{ marginBottom: 10 }}>{plant ? 'Edit plant' : 'Add plant'}</h3>

      <div className="bar">
        <div style={{ flex: 2 }}>
          <div className="sub" style={{ marginBottom: 4 }}>Plant name</div>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tipper 3" />
        </div>
        <div style={{ flex: 1 }}>
          <div className="sub" style={{ marginBottom: 4 }}>Code</div>
          <input type="text" value={code} autoCapitalize="characters" onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="TP-03" />
        </div>
      </div>

      {(costCodes.length > 0 || costCodeId) && (
        <>
          <label className="sub" htmlFor="plant-cost-code" style={{ display: 'block', margin: '10px 0 4px' }}>
            Usual cost code
          </label>
          <CostCodeSelect id="plant-cost-code" codes={costCodes} value={costCodeId} onChange={setCostCodeId} />
          <div className="sub" style={{ marginTop: 4 }}>
            Fuel and misc for this plant start with this code. The office can change it on the day.
          </div>
        </>
      )}

      <div className="fld" style={{ marginTop: 10 }}>
        <div>
          Next service due
          {servicePill ? <span className={servicePill.className} style={{ marginLeft: 8 }}>{servicePill.text}</span> : null}
          <div className="sub">
            {plant?.serviceDueOn && serviceDueOn !== plant.serviceDueOn
              ? `Was ${formatDate(plant.serviceDueOn)} — kept as history`
              : 'Leave empty if none is booked. Its cost goes in Misc.'}
          </div>
        </div>
        <input type="date" value={serviceDueOn} onChange={(e) => setServiceDueOn(e.target.value)} style={{ width: 170 }} />
      </div>

      <div className="fld">
        <div>
          Road vehicle
          <div className="sub">Has a number plate and papers that expire</div>
        </div>
        <div>
          <input type="checkbox" checked={road} onChange={(e) => setRoad(e.target.checked)} />
        </div>
      </div>

      {road && (
        <>
          <div className="sub" style={{ marginBottom: 4 }}>Registration number</div>
          <input type="text" value={reg} autoCapitalize="characters" onChange={(e) => setReg(e.target.value.toUpperCase())} placeholder="LK-4821" />

          <div className="sub" style={{ margin: '12px 0 2px' }}>
            Papers — leave a date empty if it does not apply
          </div>
          {PAPERS.map((paper) => {
            const value = dates[paper.key];
            const days = value ? daysLeft(today, value) : null;
            const pill = days === null ? null : pillFor(days, 30);
            return (
              <div className="fld" key={paper.key}>
                <div>
                  {paper.label}
                  {pill ? <span className={pill.className} style={{ marginLeft: 8 }}>{pill.text}</span> : null}
                  <div className="sub">{paper.sub}</div>
                </div>
                <input
                  type="date"
                  value={value}
                  onChange={(e) => setDates((d) => ({ ...d, [paper.key]: e.target.value }))}
                  style={{ width: 170 }}
                />
              </div>
            );
          })}
          <div className="sub">Changing a date keeps the old one as history.</div>
        </>
      )}

      {problem && <div className="banner b-no" style={{ marginTop: 8 }}>{problem}</div>}

      <div className="bar" style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
        {onRemove && (
          <button
            type="button"
            className="btn dang"
            disabled={saving}
            onClick={() => { setSaving(true); void onRemove().finally(() => setSaving(false)); }}
          >
            Remove
          </button>
        )}
        <button
          type="button"
          className="btn pri"
          disabled={saving || name.trim().length === 0 || code.trim().length === 0}
          onClick={() => {
            setProblem('');
            if (road && reg.trim().length === 0) { setProblem('A road vehicle needs its registration number.'); return; }
            setSaving(true);
            void onSave({
              name: name.trim(),
              code: code.trim(),
              costCodeId,
              isRoadVehicle: road,
              registrationNo: road ? reg.trim() : null,
              licenceExpiresOn: road ? dates.licenceExpiresOn || null : null,
              insuranceExpiresOn: road ? dates.insuranceExpiresOn || null : null,
              emissionExpiresOn: road ? dates.emissionExpiresOn || null : null,
              fitnessExpiresOn: road ? dates.fitnessExpiresOn || null : null,
              serviceDueOn: serviceDueOn || null,
            })
              .catch(() => undefined)
              .finally(() => setSaving(false));
          }}
        >
          {saving ? 'Saving…' : plant ? 'Save' : 'Add plant'}
        </button>
      </div>
    </div>
  );
}
